/**
 * Reports Page — Compliance Report Generation.
 *
 * Each report type exposes three actions, mirroring the Policies pattern:
 *   • Preview  — inline PDF preview in a modal
 *   • PDF      — download as PDF
 *   • Word     — download as DOCX (converted on the server)
 *
 * Reports are generated fresh on every action against current platform data,
 * so there is no "generated reports" history surface here.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  ShieldCheck, Lock, Shield, FileText, AlertTriangle, Bug, Eye, Globe,
  Loader2, Package, X, ChevronRight, LayoutGrid,
} from 'lucide-react';
import { Card } from '../components/ui-custom/ComplianceUI';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const REPORT_TYPES = [
  { id: 'compliance_summary',     title: 'SOC 2 Compliance Summary',          description: 'Overall posture, category breakdown, and key findings.',              icon: ShieldCheck,    color: '#2563eb' },
  { id: 'policy_coverage_matrix', title: 'Policy Coverage Matrix',            description: 'Every policy mapped to its SOC 2 controls with live pass/fail — auditor cover sheet.', icon: LayoutGrid, color: '#0ea5e9' },
  { id: 'security_overview',      title: 'Security Architecture Overview',    description: 'Infrastructure, application, and data protection controls.',          icon: Lock,           color: '#059669' },
  { id: 'control_status',         title: 'Control Status Report',             description: 'Detailed status of all SOC 2 controls.',                              icon: Shield,         color: '#7c3aed' },
  { id: 'policy_summary',         title: 'Policy Library Summary',            description: 'Overview of all compliance policies and their owners.',               icon: FileText,       color: '#d97706' },
  { id: 'findings_report',        title: 'Security Findings Report',          description: 'Active security findings and remediation status.',                    icon: AlertTriangle,  color: '#dc2626' },
  { id: 'pentest_summary',        title: 'Pen Test Summary',                  description: 'Penetration testing results and remediation status.',                 icon: Bug,            color: '#7c3aed' },
  { id: 'accessibility_report',   title: 'Accessibility Report',              description: 'WCAG compliance audit results and element scores.',                   icon: Eye,            color: '#0891b2' },
  { id: 'caiq_report',            title: 'CAIQ v4.1.0 Response',              description: 'Cloud Security Alliance questionnaire responses.',                    icon: Globe,          color: '#6b7280' },
];

export default function ReportsPage() {
  const [busy, setBusy] = useState({});   // { [reportId]: 'pdf' | 'docx' | 'preview' }
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewMeta, setPreviewMeta] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const setReportBusy = (id, kind) => setBusy((b) => ({ ...b, [id]: kind }));
  const clearReportBusy = (id) => setBusy((b) => { const c = { ...b }; delete c[id]; return c; });

  const handleExport = async (report, fmt) => {
    setReportBusy(report.id, fmt);
    try {
      const res = await fetch(
        `${API_URL}/api/reports/export/${report.id}/${fmt}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Download failed' }));
        toast.error(err.detail || 'Download failed');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.id}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Downloaded ${a.download}`);
    } catch (err) {
      console.error(err);
      toast.error('Download failed — check console for details');
    } finally {
      clearReportBusy(report.id);
    }
  };

  const handlePreview = async (report) => {
    setPreviewMeta(report);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setReportBusy(report.id, 'preview');
    try {
      const res = await fetch(
        `${API_URL}/api/reports/preview/${report.id}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Preview failed' }));
        toast.error(err.detail || 'Preview failed');
        setPreviewOpen(false);
        return;
      }
      const blob = await res.blob();
      setPreviewUrl(window.URL.createObjectURL(blob));
    } catch (err) {
      console.error(err);
      toast.error('Preview failed');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
      clearReportBusy(report.id);
    }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewMeta(null);
    setPreviewOpen(false);
  };

  return (
    <div className="reports-page">
      <div className="page-header">
        <div>
          <h1>Compliance Reports</h1>
          <p>
            Branded reports rendered live from current platform data. Each
            type is available as an inline preview, PDF download, or Word
            (.docx) export — same pattern as the Policies library.
          </p>
        </div>
      </div>

      <div className="policies-list" data-testid="reports-list">
        {REPORT_TYPES.map((report) => {
          const Icon = report.icon;
          const reportBusy = busy[report.id];
          return (
            <Card
              key={report.id}
              className="policy-card"
              data-testid={`report-${report.id}`}
            >
              <div
                className="report-icon"
                style={{
                  background: `${report.color}1f`,
                  color: report.color,
                  borderRadius: 8,
                  padding: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon size={20} />
              </div>
              <div className="policy-info">
                <div className="policy-title">{report.title}</div>
                <div className="policy-meta">{report.description}</div>
              </div>

              <button
                className="link-btn muted"
                onClick={(e) => { e.stopPropagation(); handlePreview(report); }}
                data-testid={`preview-${report.id}`}
                disabled={!!reportBusy}
                title="Preview PDF inline"
              >
                {reportBusy === 'preview'
                  ? <Loader2 size={14} className="spin" />
                  : <Eye size={14} />}
                {' '}Preview
              </button>
              <button
                className="link-btn muted"
                onClick={(e) => { e.stopPropagation(); handleExport(report, 'pdf'); }}
                data-testid={`download-pdf-${report.id}`}
                disabled={!!reportBusy}
              >
                {reportBusy === 'pdf' ? <Loader2 size={14} className="spin" /> : null}
                {reportBusy === 'pdf' ? ' Working…' : 'PDF'}
              </button>
              <button
                className="link-btn muted"
                onClick={(e) => { e.stopPropagation(); handleExport(report, 'docx'); }}
                data-testid={`download-docx-${report.id}`}
                disabled={!!reportBusy}
              >
                {reportBusy === 'docx' ? <Loader2 size={14} className="spin" /> : null}
                {reportBusy === 'docx' ? ' Working…' : 'Word'}
              </button>

              <ChevronRight size={16} className="policy-arrow" />
            </Card>
          );
        })}
      </div>

      <Card className="audit-export" style={{ marginTop: 32 }}>
        <h3>Audit Evidence Package</h3>
        <p>
          Generate a comprehensive audit-ready evidence package combining
          all controls, evidence, findings, and policy revisions in a
          single export.
        </p>
        <button
          className="btn-success"
          data-testid="generate-audit-package"
          onClick={() => toast.info('Audit package generation coming soon')}
        >
          <Package size={16} /> Generate Audit Package
        </button>
      </Card>

      {previewOpen && previewMeta && (
        <div className="modal-overlay" onClick={closePreview}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ width: '90vw', maxWidth: '1200px', height: '90vh', display: 'flex', flexDirection: 'column' }}
            data-testid="report-preview-modal"
          >
            <div className="modal-header">
              <h2>{previewMeta.title} — Preview</h2>
              <button
                className="modal-close"
                onClick={closePreview}
                data-testid="close-preview-modal"
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body" style={{ flex: 1, padding: 0, overflow: 'hidden' }}>
              {previewLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Loader2 size={32} className="spin" />
                  <span style={{ marginLeft: 12 }}>Generating preview…</span>
                </div>
              ) : previewUrl ? (
                <iframe
                  title={`${previewMeta.title} preview`}
                  src={previewUrl}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              ) : null}
            </div>
            <div className="modal-footer">
              <button
                className="link-btn muted"
                onClick={() => handleExport(previewMeta, 'pdf')}
                disabled={!!busy[previewMeta.id]}
                data-testid="preview-download-pdf"
              >
                Download PDF
              </button>
              <button
                className="link-btn muted"
                onClick={() => handleExport(previewMeta, 'docx')}
                disabled={!!busy[previewMeta.id]}
                data-testid="preview-download-docx"
              >
                Download Word
              </button>
              <button className="btn-secondary" onClick={closePreview}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
